import type { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import {
  Button,
  ButtonGroup,
  Colors,
  Icon,
  Spinner,
  type IconName,
} from "@blueprintjs/core";
import {
  createColDef,
  ItemGridFilterProfiles,
} from "@package/ui-apollo-components";
import { useToaster } from "@package/ui-components";
import {
  EventPayloadType,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import {
  DeleteLocationDocument,
  GetLocationDocument,
  GetLocationGridDocument,
  GetLocationListNameIdDocument,
  useGetLocationGridQuery,
  useGetProfileListNameIdQuery,
  type Location,
} from "@package/ui-gql/index-data-access";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ITEM_GRID_PATH_EDIT,
  LOCATION_STATUS_POLL_INTERVAL_MS,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { useGetEvents } from "../../../../hooks/use-get-events";
import { useAppSelector } from "../../../../store/store";
import { ItemGridWithToolbar } from "../../../ItemGrid/ItemGridWithToolbar";
import { WallPreviewLocation } from "../../../WallPreview/WallPreviewLocation";
import { DialogReloadLocation } from "./DialogReloadLocation";

export function LocationGrid() {
  const variables = useAppSelector(
    (state) => state.itemGrid.locations.variables,
  );
  const selectedItemId = useAppSelector(
    (state) => state.itemGrid.locations.selectedItemId,
  );
  const { search } = useLocation();
  const { toaster } = useToaster();
  const navigate = useNavigate();

  const [reloadLocationId, setReloadLocationId] = useState<string>();

  const {
    data: dataLocations,
    loading: loadingLocations,
    error: errorLocations,
  } = useGetLocationGridQuery({ variables });
  const {
    data: dataProfiles,
    loading: loadingProfiles,
    error: errorProfiles,
  } = useGetProfileListNameIdQuery({
    variables: {
      page: 0,
      perPage: 1000,
    },
  });

  const locations = useMemo(
    () =>
      (dataLocations?.items
        ?.map((location) => location.id)
        ?.filter((location) => location) as string[]) ?? [],
    [dataLocations?.items],
  );
  const variablesEvents = useMemo<GetEventsQueryVariables>(() => {
    const now = new Date();
    const start = new Date(now);
    start.setMinutes(start.getMinutes() - 10);

    return {
      filter: {
        eventType: [EventPayloadType.DeviceActive],
        location: locations,
        range: {
          start,
        },
      },
      page: 0,
      perPage: 1000,
      sortField: "timestamp",
      sortOrder: "DESC",
    };
  }, [locations]);

  const {
    data: dataEvents,
    loading: loadingEvents,
    error: errorEvents,
    refetch: refetchEvents,
  } = useGetEvents(variablesEvents, locations.length === 0);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        ...createColDef("actions", "", 0, 40),
        cellClass: "p-0",
        maxWidth: 40,
        cellRenderer: (params: ICellRendererParams<Location, string>) => {
          return (
            <div className="flex flex-row w-full h-full items-center justify-start">
              <ButtonGroup>
                <Button
                  className="outline-none"
                  disabled={!params.data?.id}
                  icon="refresh"
                  intent="primary"
                  minimal
                  outlined
                  title="Reload Location"
                  onClick={() => {
                    setReloadLocationId(params.data?.id ?? undefined);
                  }}
                />
              </ButtonGroup>
            </div>
          );
        },
      },
      {
        ...createColDef("status", "Status", 0, 80),
        cellClass: "text-center",
        maxWidth: 80,
        cellRenderer: (params: ICellRendererParams<Location, string>) => {
          const event = dataEvents?.items?.find(
            (event) => event.location === params.data?.id,
          );
          const isActive =
            event?.timestamp &&
            moment(event.timestamp).isAfter(moment().subtract(5, "minutes"));
          const isIdle =
            !isActive &&
            event?.timestamp &&
            moment(event.timestamp).isAfter(moment().subtract(10, "minutes"));

          const icon: IconName = isActive || isIdle ? "full-circle" : "circle";
          const color = isActive
            ? Colors.GREEN3
            : isIdle
              ? Colors.ORANGE3
              : Colors.GRAY3;
          const title = isActive
            ? "Active"
            : isIdle
              ? "No update in last 5 minutes"
              : "Offline";

          if (loadingEvents) {
            return <Spinner size={16} />;
          }
          if (errorEvents) {
            return (
              <Icon
                icon="help"
                size={16}
                color={Colors.RED5}
                title="Status request failed"
              />
            );
          }

          return <Icon icon={icon} size={16} color={color} title={title} />;
        },
      },
      createColDef<Location>("name", "Name", 1, 200),
      {
        ...createColDef<Location>("profile", "Canvas", 1, 150),
        cellRenderer: (params: ICellRendererParams<Location, string>) => {
          const db = dataProfiles?.items?.find((p) => p.id === params.value);
          if (db) {
            return (
              <a
                style={{ color: Colors.BLUE1 }}
                onClick={() =>
                  navigate(
                    `/${SidebarEntryPath.profiles}/${ITEM_GRID_PATH_EDIT}/${db.id}${search}`,
                  )
                }
              >
                {db.name}
              </a>
            );
          }

          return "";
        },
      },
      createColDef<Location>("updatedAt", "Last Updated", 1, 125),
    ],
    [
      dataEvents?.items,
      dataProfiles?.items,
      errorEvents,
      loadingEvents,
      navigate,
      search,
    ],
  );

  useEffect(
    function effectUpdateLocationStatus() {
      let timeout: ReturnType<typeof setTimeout>;

      const update = async () => {
        const now = new Date();
        const start = new Date(now);
        start.setMinutes(start.getMinutes() - 10);

        await refetchEvents({
          ...variablesEvents,
          filter: {
            ...variablesEvents.filter,
            range: {
              start,
            },
          },
        });
        timeout = setTimeout(() => update(), LOCATION_STATUS_POLL_INTERVAL_MS);
      };

      timeout = setTimeout(() => update(), LOCATION_STATUS_POLL_INTERVAL_MS);

      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    },
    [refetchEvents, variablesEvents],
  );

  return (
    <div className="flex flex-col w-full h-full">
      <div className="shrink">
        <ItemGridWithToolbar
          propsItemGrid={{
            sidebarEntryPath: SidebarEntryPath.locations,
            columnDefs,
            data: dataLocations,
            error: !!errorLocations || !!errorProfiles,
            loading: !dataLocations && (loadingLocations || loadingProfiles),
            filtersSupported: true,
            filterPanels: [<ItemGridFilterProfiles key="profiles" />],
          }}
          propsItemGridToolbar={{
            title: "Location",
            sidebarEntryPath: SidebarEntryPath.locations,
            createEnabled: true,
            deleteDocument: DeleteLocationDocument,
            deleteEnabled: true,
            deleteRefetchQueries: [
              GetLocationDocument,
              GetLocationGridDocument,
              GetLocationListNameIdDocument,
            ],
            editEnabled: true,
            exportEnabled: false,
          }}
        />
      </div>
      <div className="flex-0 mt-4">
        <hr />
      </div>
      <div
        className={`flex-0 mt-4 z-20 ${selectedItemId ? "sticky bottom-4" : ""}`}
      >
        <WallPreviewLocation location={selectedItemId} />
      </div>
      <DialogReloadLocation
        isOpen={!!reloadLocationId}
        locationId={reloadLocationId}
        onClose={() => {
          setReloadLocationId(undefined);
        }}
        onReload={() => {
          toaster?.show({
            message: "Location reload scheduled",
            intent: "success",
            icon: "tick-circle",
            timeout: 3000,
          });
        }}
      />
    </div>
  );
}
